
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { ReferralId, ReferralIdStatus } from '@/utils/referral/bank/types';

interface ReferralIdManagementProps {
  instanceId: string;
}

const ReferralIdManagement: React.FC<ReferralIdManagementProps> = ({ instanceId }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [generationCount, setGenerationCount] = useState(100);
  const [prefix, setPrefix] = useState('');
  const [referralIds, setReferralIds] = useState<string[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    used: 0,
    available: 0,
  });
  
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        // In a real app, fetch referral IDs and stats for this instance
        // For now, use mock data
        setReferralIds([
          'REF-12345',
          'REF-67890',
          'REF-ABCDE',
          // ... more IDs would be here
        ]);
        
        setStats({
          total: 1000,
          used: 250,
          available: 750,
        });
      } catch (error) {
        console.error('Error fetching referral data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load referral ID data',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchInitialData();
  }, [instanceId, toast]);
  
  const handleGenerateIds = async () => {
    if (generationCount <= 0 || generationCount > 1000) {
      toast({
        title: 'Invalid Input',
        description: 'Please enter a number between 1 and 1000',
        variant: 'destructive',
      });
      return;
    }
    
    setIsLoading(true);
    try {
      // In a real app, generate and save referral IDs
      // For now, just mock the operation
      const newIds: ReferralId[] = Array.from({ length: generationCount }, (_, i) => ({
        id: `ref-${Date.now()}-${i}`,
        code: `${prefix || 'REF'}${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
        userId: '',
        instanceId,
        status: 'unused' as ReferralIdStatus,
        createdAt: new Date(),
        updatedAt: new Date(),
        isAssigned: false
      }));
      
      // Update UI with new IDs
      setReferralIds(prev => [...newIds.map(id => id.code), ...prev]);
      setStats(prev => ({
        ...prev,
        total: prev.total + newIds.length,
        available: prev.available + newIds.length,
      }));
      
      toast({
        title: 'Success',
        description: `Generated ${newIds.length} new referral IDs`,
      });
    } catch (error) {
      console.error('Error generating referral IDs:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate referral IDs',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Referral ID Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-blue-500 mb-1">Total IDs</div>
              <div className="text-2xl font-bold">{stats.total}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm text-green-500 mb-1">Available</div>
              <div className="text-2xl font-bold">{stats.available}</div>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg">
              <div className="text-sm text-amber-500 mb-1">Used</div>
              <div className="text-2xl font-bold">{stats.used}</div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="generationCount">Number of IDs to Generate</Label>
                <Input 
                  id="generationCount"
                  type="number"
                  min="1"
                  max="1000"
                  value={generationCount}
                  onChange={(e) => setGenerationCount(parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prefix">Prefix (Optional)</Label>
                <Input 
                  id="prefix"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="e.g., PROMO"
                />
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={handleGenerateIds} 
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? 'Generating...' : 'Generate IDs'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Referral ID List</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All IDs</TabsTrigger>
              <TabsTrigger value="available">Available</TabsTrigger>
              <TabsTrigger value="used">Used</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="space-y-4">
              {isLoading ? (
                <div className="text-center py-8">Loading referral IDs...</div>
              ) : referralIds.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No referral IDs found. Generate some using the form above.
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <div className="grid grid-cols-4 bg-muted p-3 font-medium">
                    <div>Referral ID</div>
                    <div>Status</div>
                    <div>Created</div>
                    <div>Used By</div>
                  </div>
                  <div className="divide-y">
                    {referralIds.slice(0, 10).map((id, index) => (
                      <div key={id} className="grid grid-cols-4 p-3">
                        <div className="font-mono">{id}</div>
                        <div>
                          <span className="inline-block px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                            Available
                          </span>
                        </div>
                        <div className="text-muted-foreground text-sm">
                          {new Date().toLocaleDateString()}
                        </div>
                        <div className="text-muted-foreground">-</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="available">
              <div className="text-center py-8 text-muted-foreground">
                Filter view for available referral IDs would be shown here
              </div>
            </TabsContent>
            
            <TabsContent value="used">
              <div className="text-center py-8 text-muted-foreground">
                Filter view for used referral IDs would be shown here
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralIdManagement;
